/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IHTTPRequestOptions } from '@msdyn365-commerce/core-internal/dist/types';
/**
 * SendBulkOrderEmail Input Action
 */

export class SendBulkOrderEmailInput implements Msdyn365.IActionInput {
    public yourName: string;
    public position: string;
    public schoolName: string;
    public schoolAddress: string;
    public bestContactNumber: string;
    public contactEmailAddress: string;
    public enquiryDetails: string;
    public subject: string;
    public mandsEmailAddress: string;
    public emailRelayLink: string;
    public templateId: string;
    public mandsEmailSenderName: string;

    constructor(
        yourName: string,
        position: string,
        schoolName: string,
        schoolAddress: string,
        bestContactNumber: string,
        contactEmailAddress: string,
        enquiryDetails: string,
        subject: string,
        mandsEmailAddress: string,
        emailRelayLink: string,
        templateId: string,
        mandsEmailSenderName: string
    ) {
        this.yourName = yourName;
        this.position = position;
        this.schoolName = schoolName;
        this.schoolAddress = schoolAddress;
        this.bestContactNumber = bestContactNumber;
        this.contactEmailAddress = contactEmailAddress;
        this.enquiryDetails = enquiryDetails;
        this.subject = subject;
        this.mandsEmailAddress = mandsEmailAddress;
        this.emailRelayLink = emailRelayLink;
        this.templateId = templateId;
        this.mandsEmailSenderName = mandsEmailSenderName;
    }

    public getCacheKey = () => `BulkOrderEmail`;
    public getCacheObjectType = () => 'BulkOrderEmail';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface ISendBulkOrderEmailData {
    data: string;
    status: number;
}
export interface IEmailRelayData {
    repsonseMessage: string;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    throw new Error('Unable to call this data action on page load.');
};

async function action(input: SendBulkOrderEmailInput, ctx: Msdyn365.IActionContext): Promise<ISendBulkOrderEmailData> {
    const {
        yourName,
        position,
        schoolName,
        schoolAddress,
        bestContactNumber,
        contactEmailAddress,
        enquiryDetails,
        subject,
        emailRelayLink,
        mandsEmailAddress,
        templateId,
        mandsEmailSenderName
    } = input;
    const options: IHTTPRequestOptions = {
        headers: {
            'Content-Type': 'application/json'
        },
        timeout: 10000
    };
    const emailBody = {
        replacements: {
            name: yourName,
            position: position,
            schoolname: schoolName,
            SchoolAddress: schoolAddress,
            ContactNumber: bestContactNumber,
            ContactEmail: contactEmailAddress,
            Enquiry: enquiryDetails
        },
        recipientEmailAddresses: {
            [mandsEmailSenderName]: mandsEmailAddress
        },
        senderEmailAddress: {
            'No reply': contactEmailAddress
        },
        templateData: {
            templateId: templateId
        },
        subjectData: {
            Subject: subject
        }
    };

    const response = await Msdyn365.sendRequest<IEmailRelayData>(emailRelayLink, 'post', JSON.stringify(emailBody), options);
    const responseMessage = response.data && response.data.repsonseMessage ? response.data.repsonseMessage : '';
    const statusCode = response.status ? response.status : 500;
    return { data: responseMessage, status: statusCode };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISendBulkOrderEmailData>>action,
    input: createInput
});
